import { useState, useEffect, useRef } from 'react';

const concerns = [
  'You should be further ahead in life by now',
  'You are ready for change but unsure where to start',
  'You have goals, but no clear plan to reach them',
  'You are overwhelmed by the gap between where you are and where you want to be',
];

export default function ProblemSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="problem" className="bg-white py-24 md:py-32" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        {/* Headline */}
        <h2
          className={`text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ fontFamily: '\'Playfair Display\', serif' }}
        >
          Feeling Stuck? Frustrated? Behind?
        </h2>

        <p
          className={`text-xl md:text-2xl text-[#6B6B6B] mb-12 leading-relaxed italic transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ fontFamily: '\'Playfair Display\', serif' }}
        >
          Sometimes life can leave you feeling like everyone else is moving forward
          while you are standing still.
        </p>

        {/* Poetic Lines */}
        <div
          className={`space-y-4 mb-14 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ fontFamily: '\'Playfair Display\', serif' }}
        >
          <p className="text-2xl md:text-3xl text-[#2C2C2C]">Maybe you have goals.</p>
          <p className="text-2xl md:text-3xl text-[#2C2C2C] italic">Maybe you have dreams.</p>
          <p className="text-2xl md:text-3xl text-[#2C2C2C]">
            Maybe deep down you know you are <strong>capable of more.</strong>
          </p>
        </div>

        <p
          className={`text-lg text-[#6B6B6B] mb-10 leading-relaxed transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          But somewhere along the way, you lost momentum, confidence, or direction.
        </p>

        {/* Concerns List */}
        <div
          className={`text-left max-w-2xl mx-auto mb-14 space-y-4 transition-all duration-700 delay-400 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <p className="text-sm font-semibold text-[#D4896A] uppercase tracking-widest mb-6 text-center">
            You may be feeling like:
          </p>
          {concerns.map((concern, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-2 h-2 rounded-full bg-[#D4896A] mt-2.5 shrink-0" />
              <p className="text-lg text-[#2C2C2C] leading-relaxed">{concern}</p>
            </div>
          ))}
        </div>

        {/* Reassurance Block */}
        <div
          className={`bg-[#F5F2EC] rounded-2xl px-8 md:px-12 py-10 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p
            className="text-2xl md:text-3xl font-semibold text-[#2C2C2C] leading-snug mb-3"
            style={{ fontFamily: '\'Playfair Display\', serif' }}
          >
            The good news is this: <span className="text-[#D4896A]">change is possible.</span>
          </p>
          <p
            className="text-lg text-[#6B6B6B] leading-relaxed italic"
            style={{ fontFamily: '\'Playfair Display\', serif' }}
          >
            Sometimes all it takes is the right support, the right strategy, and the right next step.
          </p>
        </div>
      </div>
    </section>
  );
}
